const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function buildAuthHeaders(accessToken) {
  return {
    Authorization: `Bearer ${accessToken}`,
  };
}

async function parseJsonSafely(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

function extractApiError(data, fallbackMessage) {
  return (
    data?.detail ||
    data?.file?.[0] ||
    data?.amount?.[0] ||
    data?.renovation_type?.[0] ||
    data?.notes?.[0] ||
    data?.non_field_errors?.[0] ||
    fallbackMessage
  );
}

/* =========================================================
   CONTEXT URL
   ========================================================= */

function buildInvoiceContextUrl({ loanCaseId = null, projectId = null }) {
  if (loanCaseId) {
    return `${API_BASE_URL}/api/loan-cases/${loanCaseId}/renovation-invoices/`;
  }

  if (projectId) {
    return `${API_BASE_URL}/api/projects/${projectId}/renovation-invoices/`;
  }

  throw new Error("No loan case or renovation project is available.");
}

/* =========================================================
   UPLOAD
   ========================================================= */

export async function uploadRenovationInvoice({
  accessToken,
  loanCaseId = null,
  projectId = null,
  file,
  renovationType,
  amount,
  notes = "",
}) {
  const url = buildInvoiceContextUrl({
    loanCaseId,
    projectId,
  });

  const formData = new FormData();

  formData.append("file", file);
  formData.append("renovation_type", renovationType);
  formData.append("amount", amount);
  formData.append("notes", notes || "");

  const response = await fetch(url, {
    method: "POST",

    headers: buildAuthHeaders(accessToken),

    body: formData,
  });

  const data = await parseJsonSafely(response);

  if (!response.ok) {
    throw new Error(
      extractApiError(data, "Could not upload renovation invoice."),
    );
  }

  return data;
}

/* =========================================================
   LIST
   ========================================================= */

export async function listRenovationInvoices({
  accessToken,
  loanCaseId = null,
  projectId = null,
}) {
  const url = buildInvoiceContextUrl({
    loanCaseId,
    projectId,
  });

  const response = await fetch(url, {
    method: "GET",

    headers: buildAuthHeaders(accessToken),
  });

  const data = await parseJsonSafely(response);

  if (!response.ok) {
    throw new Error(
      extractApiError(data, "Could not load renovation invoices."),
    );
  }

  return data;
}

/* =========================================================
   DELETE
   ========================================================= */

export async function deleteRenovationInvoice({ accessToken, invoiceId }) {
  const response = await fetch(
    `${API_BASE_URL}/api/renovation-invoices/${invoiceId}/delete/`,
    {
      method: "DELETE",
      headers: buildAuthHeaders(accessToken),
    },
  );

  if (!response.ok) {
    const data = await parseJsonSafely(response);

    throw new Error(extractApiError(data, "Could not delete invoice."));
  }

  return true;
}

/* =========================================================
   REPLACE
   ========================================================= */

export async function replaceRenovationInvoice({
  accessToken,
  invoiceId,
  renovationType,
  amount,
  notes,
  file,
}) {
  const formData = new FormData();

  formData.append("renovation_type", renovationType);

  formData.append("amount", amount);
  formData.append("notes", notes || "");

  if (file) {
    formData.append("file", file);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/renovation-invoices/${invoiceId}/replace/`,
    {
      method: "POST",
      headers: buildAuthHeaders(accessToken),
      body: formData,
    },
  );

  const data = await parseJsonSafely(response);

  if (!response.ok) {
    throw new Error(extractApiError(data, "Could not update invoice."));
  }

  return data;
}

/* =========================================================
   DETAIL
   ========================================================= */

export async function getRenovationInvoiceDetail({ accessToken, invoiceId }) {
  const response = await fetch(
    `${API_BASE_URL}/api/renovation-invoices/${invoiceId}/`,
    {
      method: "GET",
      headers: buildAuthHeaders(accessToken),
    },
  );

  const data = await parseJsonSafely(response);

  if (!response.ok) {
    throw new Error(extractApiError(data, "Could not load invoice details."));
  }

  return data;
}

/* =========================================================
   FILE
   ========================================================= */

export async function fetchRenovationInvoiceFile({ accessToken, invoiceId }) {
  const response = await fetch(
    `${API_BASE_URL}/api/renovation-invoices/${invoiceId}/file/`,
    {
      method: "GET",

      headers: buildAuthHeaders(accessToken),
    },
  );

  if (!response.ok) {
    const data = await parseJsonSafely(response);

    throw new Error(extractApiError(data, "Could not load invoice file."));
  }

  return response.blob();
}
